
import { Component, Vue, Watch } from "vue-property-decorator";
import Sidebar from "@/components/navigations/sidebar.vue";
import Cta from "@/components/buttons/Button.vue";
import Card from "@/components/card/card.vue";
import Tag from "@/components/tags/Tag.vue";
import Tabs from "@/components/tabs/Tab.vue";
import {
  Benevole,
  BenevoleDetail,
  CompetenceGroup,
  CompetenceItem,
  CompetenceStep,
} from "@/models";
import { benevoles, login } from "@/store";
import BenevoleService from "@/services/benevole.service";
import debounce from "lodash/debounce";

interface PanelData {
  benevole: BenevoleDetail;
  competence: CompetenceItem;
}

@Component({
  components: { Sidebar, Cta, Card, Tag, Tabs },
})
export default class BenevolesView extends Vue {
  dataReady = false;
  search = "";
  tabSelected = "liste";
  isMobile = false;
  private resizeHandler!: () => void;
  readonly allTabs = [
    {
      key: "liste",
      label: "Liste",
      allowedRoles: ["ADMIN", "USR:VIEW", "USR:COMP"],
      hideOnMobile: false,
    },
    {
      key: "tableau",
      label: "Tableau",
      allowedRoles: ["ADMIN", "USR:COMP"],
      hideOnMobile: true,
    },
  ];

  tableauLoading = false;
  tableauLoaded = false;
  tableauData: BenevoleDetail[] = [];

  panel: PanelData | null = null;
  panelSaving = false;
  panelSaved = false;

  private saveComment!: () => void;

  created(): void {
    this.saveComment = debounce(async () => {
      if (!this.panel) return;
      this.panelSaving = true;
      await BenevoleService.updateCompetence(
        this.panel.benevole.id,
        this.panel.competence.id,
        this.panel.competence.step.id,
        this.panel.competence.comment,
      );
      this.panelSaving = false;
      this.panelSaved = true;
      setTimeout(() => (this.panelSaved = false), 2000);
    }, 1500);
  }

  async mounted(): Promise<void> {
    this.isMobile = window.innerWidth <= 1122;
    this.resizeHandler = () => {
      this.isMobile = window.innerWidth <= 1122;
    };
    window.addEventListener("resize", this.resizeHandler);

    await benevoles.benevoles();
    this.dataReady = true;
    if (!this.filteredTabs.find((t) => t.key === this.tabSelected)) {
      this.tabSelected = this.filteredTabs[0]?.key ?? "liste";
    }
  }

  beforeDestroy(): void {
    window.removeEventListener("resize", this.resizeHandler);
  }

  @Watch("tabSelected")
  async onTabChange(val: string): Promise<void> {
    if (val === "tableau" && !this.tableauLoaded) {
      await this.loadTableau();
    }
  }

  private async loadTableau(): Promise<void> {
    const actifs = this.benevoles?.filter((b) => b.role !== "X") ?? [];
    if (!actifs.length) return;
    this.tableauLoading = true;
    const results = await Promise.all(
      actifs.map((b) => BenevoleService.benevole(b.id)),
    );
    this.tableauData = results.sort((a, b) => {
      const nomCompare = (a.nom ?? "").localeCompare(b.nom ?? "", "fr", {
        sensitivity: "base",
      });
      if (nomCompare !== 0) return nomCompare;
      return (a.prenom ?? "").localeCompare(b.prenom ?? "", "fr", {
        sensitivity: "base",
      });
    });
    this.tableauLoaded = true;
    this.tableauLoading = false;
  }

  // ---- colonnes ----

  get allGroups(): CompetenceGroup[] {
    return this.tableauData[0]?.competences ?? [];
  }

  get allCompetences(): CompetenceItem[] {
    return this.allGroups.flatMap((g) => g.competence);
  }

  get allSteps(): CompetenceStep[] {
    return this.tableauData[0]?.competences_steps ?? [];
  }

  groupColor(libelle: string): string {
    const n = libelle.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (n.includes("operat") || n.includes("suppl")) return "f7b32b";
    if (n.includes("social") || n.includes("nautique")) return "39a0ed";
    if (n.includes("format") || n.includes("soutien") || n.includes("conduite"))
      return "1dd3b0";
    if (n.includes("divers")) return "f282a7";
    return "6b7280";
  }

  // ---- cellules ----

  private findCompetence(
    bnv: BenevoleDetail,
    competenceId: string,
  ): CompetenceItem | undefined {
    for (const group of bnv.competences ?? []) {
      const found = group.competence.find((c) => c.id === competenceId);
      if (found) return found;
    }
    return undefined;
  }

  getCellStyle(
    bnv: BenevoleDetail,
    competenceId: string,
  ): Record<string, string> {
    const c = this.findCompetence(bnv, competenceId);
    if (!c || !c.step?.bg) {
      return { backgroundColor: "#f3f4f6", border: "1px solid #d1d5db" };
    }
    return {
      backgroundColor: `#${c.step.bg}44`,
      border: `1.5px solid #${c.step.bg}`,
    };
  }

  getCellStep(bnv: BenevoleDetail, competenceId: string): string {
    const c = this.findCompetence(bnv, competenceId);
    return c?.step?.libelle ?? "-";
  }

  hasComment(bnv: BenevoleDetail, competenceId: string): boolean {
    const c = this.findCompetence(bnv, competenceId);
    return !!(c?.comment && c.comment.length > 0);
  }

  // ---- panel ----

  openPanel(bnv: BenevoleDetail, c: CompetenceItem): void {
    const live = this.findCompetence(bnv, c.id);
    if (!live) return;
    this.panel = { benevole: bnv, competence: live };
    this.panelSaved = false;
    this.panelSaving = false;
  }

  closePanel(): void {
    this.panel = null;
  }

  async onPanelStepChange(event: Event): Promise<void> {
    if (!this.panel) return;
    const newStepId = (event.target as HTMLSelectElement).value;
    const step = this.allSteps.find((s) => s.id === newStepId);
    if (step) this.$set(this.panel.competence, "step", { ...step });
    await BenevoleService.updateCompetence(
      this.panel.benevole.id,
      this.panel.competence.id,
      newStepId,
      this.panel.competence.comment,
    );
    this.panelSaved = true;
    setTimeout(() => (this.panelSaved = false), 2000);
  }

  onPanelCommentInput(): void {
    this.panelSaving = true;
    this.panelSaved = false;
    this.saveComment();
  }

  // ---- navigation ----

  private createBenevole(): void {
    this.$router.push("/administration/benevoles/creer");
  }

  private goToUser(id: string): void {
    this.$router.push(`/administration/benevole/${id}`);
  }

  get benevoles(): Benevole[] | null {
    return benevoles.getBenevoles ?? null;
  }

  get filteredBenevoles(): Benevole[] | null {
    if (!this.benevoles) return null;
    const q = this.search.trim().toLowerCase();
    if (!q) return this.benevoles;
    return this.benevoles.filter(
      (b) =>
        b.nom_usage.toLowerCase().includes(q) ||
        b.prenom.toLowerCase().includes(q) ||
        b.nom.toLowerCase().includes(q) ||
        (b.username?.toLowerCase().includes(q) ?? false),
    );
  }

  get filteredTabs(): { key: string; label: string }[] {
    const ctx = login.getContext;
    return this.allTabs.filter(
      (tab) =>
        !(tab.hideOnMobile && this.isMobile) &&
        tab.allowedRoles.some(
          (r) => ctx?.roles.includes(r) || ctx?.habilitations.includes(r),
        ),
    );
  }

  @Watch("isMobile")
  onIsMobileChange(): void {
    if (!this.filteredTabs.find((t) => t.key === this.tabSelected)) {
      this.tabSelected = this.filteredTabs[0]?.key ?? "liste";
    }
  }
}
